import type { StreamObserver } from "./observer";
import type { OakStreamInternalOptions, OakStreamState } from "./types";

import { createInitialStreamState } from "./state";
import { streamSideSideEventsIterator } from "./streamIterator";
import { traceOakStream } from "./trace";

interface StreamConfiguration {
  hash: string;
  url: string;
  getIdTokenRefreshIfExpired: OakStreamInternalOptions["getIdTokenRefreshIfExpired"];
  initialLastEventId?: string;
  removeFromCache: (hash: string) => void;
}

interface StreamRuntime {
  connectionPromise: Promise<void> | null;
  startPromise: Promise<void> | null;
  abortController: AbortController | null;
  observers: Set<StreamObserver>;
  gcTimeout: ReturnType<typeof setTimeout> | null;
  /**
   * Monotonic generation counter for the underlying network connection owned by
   * this cached `Stream` instance.
   *
   * The stream hash guarantees that observers with the same inputs share one
   * `Stream` object, but that object can still create multiple sequential fetches
   * over its lifetime. For example, when the last observer unmounts we abort the
   * active SSE request but keep the `Stream` in cache for a short period. If a
   * new observer mounts before the aborted request has fully unwound, the same
   * `Stream` instance can start a new fetch while the previous one is still
   * resolving its `catch`/`finally` path.
   *
   * `connectionId` lets us treat each fetch attempt as its own generation and
   * ignore late state updates from older generations. Without it, an older
   * aborted connection could incorrectly overwrite the status, emit an error, or
   * clear runtime bookkeeping that belongs to the newer active connection.
   */
  connectionId: number;
  isClosed: boolean;
  /**
   * Bounded exponential backoff state. `retryAttempt` increments per failed
   * connection (network error or unexpected close), and resets to 0 on every
   * received event so a long-lived connection that drops late doesn't get
   * penalized for an earlier failure. `retryTimeout` holds the pending retry
   * so we can cancel it on close/abort.
   */
  retryAttempt: number;
  retryTimeout: ReturnType<typeof setTimeout> | null;
}

const DEFAULT_GC_TIME = 5 * 60 * 1000;
const RETRY_BASE_DELAY_MS = 500;
const RETRY_MAX_DELAY_MS = 30_000;
const RETRY_MAX_JITTER_MS = 250;
const RETRY_MAX_ATTEMPTS = 8;

function computeRetryDelayMs(attempt: number): number {
  const exponential = RETRY_BASE_DELAY_MS * 2 ** Math.max(0, attempt);
  const capped = Math.min(exponential, RETRY_MAX_DELAY_MS);
  const jitter = Math.floor(Math.random() * RETRY_MAX_JITTER_MS);
  return capped + jitter;
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

export class Stream {
  private url: string;
  private getIdTokenRefreshIfExpired: OakStreamInternalOptions["getIdTokenRefreshIfExpired"];
  private state: OakStreamState;
  private readonly removeFromCache: (hash: string) => void;
  private readonly runtime: StreamRuntime = {
    connectionPromise: null,
    startPromise: null,
    abortController: null,
    observers: new Set(),
    gcTimeout: null,
    connectionId: 0,
    isClosed: false,
    retryAttempt: 0,
    retryTimeout: null,
  };

  readonly hash: string;

  constructor(configuration: StreamConfiguration) {
    this.hash = configuration.hash;
    this.url = configuration.url;
    this.getIdTokenRefreshIfExpired = configuration.getIdTokenRefreshIfExpired;
    this.state = createInitialStreamState(configuration.initialLastEventId);
    this.removeFromCache = configuration.removeFromCache;
  }

  getUrl() {
    return this.url;
  }

  getState() {
    return this.state;
  }

  setState(nextState: OakStreamState) {
    this.state = nextState;
    this.notifyObservers();
  }

  addObserver(observer: StreamObserver) {
    if (this.runtime.isClosed) {
      return;
    }

    this.runtime.observers.add(observer);
    traceOakStream("stream", "addObserver", {
      url: this.url,
      hash: this.hash,
      observerCount: this.runtime.observers.size,
      lastEventId: this.state.lastEventId,
      status: this.state.status,
    });
    this.clearGcTimeout();
  }

  removeObserver(observer: StreamObserver) {
    this.runtime.observers.delete(observer);
    traceOakStream("stream", "removeObserver", {
      url: this.url,
      hash: this.hash,
      observerCount: this.runtime.observers.size,
      lastEventId: this.state.lastEventId,
      status: this.state.status,
    });

    if (this.runtime.isClosed) {
      return;
    }

    if (this.runtime.observers.size === 0) {
      if (this.runtime.abortController !== null) {
        traceOakStream("stream", "abortForNoObservers", {
          url: this.url,
          hash: this.hash,
          lastEventId: this.state.lastEventId,
        });
        this.runtime.abortController.abort();
      }

      this.setState({
        ...this.state,
        status: "closed",
        error: null,
      });
      this.scheduleGc();
    }
  }

  connect() {
    if (this.runtime.isClosed) {
      return Promise.resolve();
    }

    if (
      this.runtime.connectionPromise !== null &&
      this.runtime.abortController?.signal.aborted !== true
    ) {
      return this.runtime.startPromise ?? Promise.resolve();
    }

    return this.startConnection(false);
  }

  restart() {
    if (this.runtime.isClosed) {
      return Promise.resolve();
    }

    return this.startConnection(true);
  }

  /**
   * Periodic watchdog hook from the StreamCache. Forces a reconnect *only*
   * when the stream is reporting `connected` but hasn't seen traffic for at
   * least `staleAfterMs` — guards against a dead socket the OS hasn't surfaced
   * yet. Closed/errored streams are left alone here so this can't bypass the
   * bounded-retry budget; the Stream's own backoff loop handles those.
   */
  checkStale(staleAfterMs: number): void {
    if (this.runtime.isClosed || this.runtime.observers.size === 0) {
      return;
    }

    if (this.state.status !== "connected") {
      return;
    }

    const updatedAt = this.state.updatedAt ?? 0;
    if (updatedAt <= 0 || Date.now() - updatedAt <= staleAfterMs) {
      return;
    }

    traceOakStream("stream", "kickStale", {
      url: this.url,
      hash: this.hash,
      updatedAt,
      staleAfterMs,
      lastEventId: this.state.lastEventId,
    });
    this.runtime.retryAttempt = 0;
    this.clearRetryTimeout();
    void this.startConnection(true);
  }

  /**
   * User-driven nudge — visibilitychange (tab back to foreground) and
   * pageshow (bfcache restore). Resumes immediately from a closed/errored
   * state and resets the retry budget, since the user has explicitly
   * re-engaged with the page and that's a strong signal we should try again.
   */
  resume(): void {
    if (this.runtime.isClosed || this.runtime.observers.size === 0) {
      return;
    }

    const status = this.state.status;
    if (status === "closed" || status === "error") {
      this.runtime.retryAttempt = 0;
      this.clearRetryTimeout();
      void this.startConnection(true);
    }
  }

  close() {
    if (this.runtime.isClosed) {
      return;
    }

    traceOakStream("stream", "close", {
      url: this.url,
      hash: this.hash,
      lastEventId: this.state.lastEventId,
      status: this.state.status,
    });
    this.runtime.isClosed = true;
    this.runtime.connectionId += 1;
    this.clearGcTimeout();
    this.clearRetryTimeout();

    if (this.runtime.abortController !== null) {
      this.runtime.abortController.abort();
    }

    this.runtime.connectionPromise = null;
    this.runtime.startPromise = null;
    this.runtime.abortController = null;
    this.runtime.observers.clear();
    this.state = {
      ...this.state,
      status: "closed",
      error: null,
    };

    this.removeFromCache(this.hash);
  }

  private startConnection(force: boolean) {
    if (this.runtime.isClosed) {
      return Promise.resolve();
    }

    // Cancel any pending retry — we're either restarting voluntarily or
    // skipping straight past the backoff window.
    this.clearRetryTimeout();

    if (
      !force &&
      this.runtime.connectionPromise !== null &&
      this.runtime.abortController?.signal.aborted !== true
    ) {
      return this.runtime.startPromise ?? Promise.resolve();
    }

    if (force && this.runtime.abortController !== null) {
      traceOakStream("stream", "abortForRestart", {
        url: this.url,
        hash: this.hash,
        lastEventId: this.state.lastEventId,
      });
      this.runtime.abortController.abort();
    }

    const nextConnectionId = this.runtime.connectionId + 1;
    const abortController = new AbortController();
    let resolveStarted: (() => void) | undefined;
    const startPromise = new Promise<void>((resolve) => {
      resolveStarted = resolve;
    });

    this.runtime.connectionId = nextConnectionId;
    this.runtime.startPromise = startPromise;
    this.runtime.abortController = abortController;
    traceOakStream("stream", "startConnection", {
      url: this.url,
      hash: this.hash,
      force,
      connectionId: nextConnectionId,
      lastEventId: this.state.lastEventId,
      observerCount: this.runtime.observers.size,
    });
    this.setState({
      ...this.state,
      status: "connecting",
      error: null,
    });

    const connectionPromise = streamSideSideEventsIterator({
      getIdTokenRefreshIfExpired: this.getIdTokenRefreshIfExpired,
      lastEventId: this.state.lastEventId,
      signal: abortController.signal,
      url: this.url,
    })
      .then(async (stream) => {
        if (this.runtime.connectionId !== nextConnectionId || abortController.signal.aborted) {
          resolveStarted?.();
          return;
        }

        traceOakStream("stream", "connected", {
          url: this.url,
          hash: this.hash,
          connectionId: nextConnectionId,
          lastEventId: this.state.lastEventId,
        });
        this.setState({
          ...this.state,
          status: "connected",
          error: null,
        });
        resolveStarted?.();
        resolveStarted = undefined;

        for await (const event of stream) {
          if (this.runtime.connectionId !== nextConnectionId || abortController.signal.aborted) {
            return;
          }

          traceOakStream("stream", "event", {
            url: this.url,
            hash: this.hash,
            connectionId: nextConnectionId,
            eventId: event.id,
            eventName: event.event,
          });
          // A successfully delivered event proves the path is healthy — reset
          // the backoff counter so a *later* drop doesn't inherit penalties
          // from a *previous* drop.
          this.runtime.retryAttempt = 0;
          this.setState({
            ...this.state,
            status: "connected",
            data: [...this.state.data, event],
            error: null,
            lastEventId: event.id,
            updatedAt: Date.now(),
          });
        }

        if (this.runtime.connectionId === nextConnectionId) {
          traceOakStream("stream", "iteratorClosed", {
            url: this.url,
            hash: this.hash,
            connectionId: nextConnectionId,
            lastEventId: this.state.lastEventId,
          });
          this.setState({
            ...this.state,
            status: "closed",
            error: null,
            updatedAt: this.state.updatedAt || Date.now(),
          });
          this.scheduleRetryIfNeeded("close");
        }
      })
      .catch((error: unknown) => {
        if (this.runtime.connectionId !== nextConnectionId) {
          resolveStarted?.();
          return;
        }

        if (abortController.signal.aborted || isAbortError(error)) {
          resolveStarted?.();
          resolveStarted = undefined;
          traceOakStream("stream", "aborted", {
            url: this.url,
            hash: this.hash,
            connectionId: nextConnectionId,
            lastEventId: this.state.lastEventId,
          });
          this.setState({
            ...this.state,
            status: "closed",
            error: null,
          });
          return;
        }

        const normalizedError =
          error instanceof Error ? error : new Error(String(error ?? "Stream failed."));

        resolveStarted?.();
        resolveStarted = undefined;
        traceOakStream("stream", "error", {
          url: this.url,
          hash: this.hash,
          connectionId: nextConnectionId,
          message: normalizedError.message,
        });
        this.setState({
          ...this.state,
          status: "error",
          error: normalizedError,
          updatedAt: Date.now(),
        });
        this.scheduleRetryIfNeeded("error");
      })
      .finally(() => {
        traceOakStream("stream", "connectionFinally", {
          url: this.url,
          hash: this.hash,
          connectionId: nextConnectionId,
          observerCount: this.runtime.observers.size,
          isClosed: this.runtime.isClosed,
        });
        if (this.runtime.isClosed) {
          return;
        }

        if (
          this.runtime.connectionId === nextConnectionId &&
          this.runtime.connectionPromise === connectionPromise
        ) {
          this.runtime.connectionPromise = null;
          this.runtime.startPromise = null;
          this.runtime.abortController = null;
        }

        if (this.runtime.observers.size === 0) {
          this.scheduleGc();
        }
      });

    this.runtime.connectionPromise = connectionPromise;

    return startPromise;
  }

  private notifyObservers() {
    for (const observer of this.runtime.observers) {
      observer.onStreamUpdated();
    }
  }

  private clearGcTimeout() {
    if (this.runtime.gcTimeout !== null) {
      clearTimeout(this.runtime.gcTimeout);
      this.runtime.gcTimeout = null;
    }
  }

  private clearRetryTimeout() {
    if (this.runtime.retryTimeout !== null) {
      clearTimeout(this.runtime.retryTimeout);
      this.runtime.retryTimeout = null;
    }
  }

  // After an unexpected close or transport error, reconnect with bounded
  // exponential backoff as long as observers are still attached and we haven't
  // been explicitly closed. The backoff counter is reset each time we receive
  // a stream event, so a long-lived connection that drops late retries fast.
  private scheduleRetryIfNeeded(cause: "close" | "error") {
    if (this.runtime.isClosed || this.runtime.observers.size === 0) {
      return;
    }
    if (this.runtime.retryTimeout !== null) {
      return;
    }
    if (this.runtime.retryAttempt >= RETRY_MAX_ATTEMPTS) {
      traceOakStream("stream", "retryGiveUp", {
        url: this.url,
        hash: this.hash,
        cause,
        attempt: this.runtime.retryAttempt,
        lastEventId: this.state.lastEventId,
      });
      return;
    }

    const delayMs = computeRetryDelayMs(this.runtime.retryAttempt);
    const attempt = this.runtime.retryAttempt + 1;
    this.runtime.retryAttempt = attempt;
    traceOakStream("stream", "retryScheduled", {
      url: this.url,
      hash: this.hash,
      cause,
      attempt,
      delayMs,
      lastEventId: this.state.lastEventId,
    });
    this.runtime.retryTimeout = setTimeout(() => {
      this.runtime.retryTimeout = null;
      if (this.runtime.isClosed || this.runtime.observers.size === 0) {
        return;
      }
      void this.startConnection(true);
    }, delayMs);
  }

  private scheduleGc() {
    if (this.runtime.isClosed) {
      return;
    }

    this.clearGcTimeout();

    this.runtime.gcTimeout = setTimeout(() => {
      if (this.runtime.observers.size > 0) {
        return;
      }

      if (this.runtime.connectionPromise !== null) {
        this.scheduleGc();
        return;
      }

      this.removeFromCache(this.hash);
    }, DEFAULT_GC_TIME);
  }
}
